import { Icon, IconProps } from '@iconify/react';
import { Table, TableProps, createPolymorphicComponent, } from '@mantine/core';
import React from 'react';

import styled from 'styled-components'

const _StyledTable = styled(Table)`
    border-spacing: 0px 2px;
    border-collapse: separate !important;
    & thead tr th {
        border-bottom: 0;
        padding: 10px;
    }
    & tbody tr td {
        border-bottom: 0;
        padding: 10px;
    }
    & tbody tr:has(:hover) {
        cursor: pointer;
        [data-mantine-color-scheme='light'] & {
            background-color: var(--mantine-color-gray-2);
        }
        [data-mantine-color-scheme='dark'] & {
            background-color: var(--mantine-color-dark-8);
        }
    }
`
const StyledTable = createPolymorphicComponent<'table', TableProps>(_StyledTable)

export const DataTableComponent: React.FC<{children?: React.ReactNode}> = ({ children, ...props }) => {
    return <StyledTable
        {...props}
    >
        {children}
    </StyledTable>
}

const Head = styled.thead`
    & tr {
        border-radius: var(--mantine-radius-sm);
        :where([data-mantine-color-scheme='light']) & {
            background-color: var(--mantine-color-gray-2);
            outline: 1px solid var(--mantine-color-gray-2);
        }
        :where([data-mantine-color-scheme='dark']) & {
            background-color: var(--mantine-color-gray-9);
            outline: 1px solid var(--mantine-color-gray-9);
        }
    }
`

const Body = styled.tbody`
    & tr {
        border-radius: var(--mantine-radius-sm);
        :where([data-mantine-color-scheme='light']) & {
            background-color: var(--mantine-color-white);
        }
        :where([data-mantine-color-scheme='dark']) & {
            background-color: var(--mantine-color-dark-6);
        }
    }
`

const Row = styled.tr`
    & td:first-of-type {
        border-radius: 4px 0 0 4px;
    }
    & td:last-child {
        border-radius: 0 4px 4px 0;
    }
    & th:first-of-type {
        border-radius: 4px 0 0 4px;
    }
    & th:last-of-type {
        border-radius: 0 4px 4px 0;
    }
    &.selected {
        outline: 1px solid var(--mantine-color-green-5);
    }
`

const Cell = styled.td`
`

const Heading = styled.th`
    text-transform: capitalize;
    & a {
        font-size: var(--mantine-font-size-xs);
        font-weight: 500;
    }
`

const SortLink = styled.a`
    line-height: initial;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    [data-mantine-color-scheme='light'] & {
        color: var(--mantine-color-gray-6);
    }
    [data-mantine-color-scheme='dark'] & {
        color: var(--mantine-color-dark-3);
    } 
    &.sorted {
        [data-mantine-color-scheme='light'] & {
            color: var(--mantine-color-gray-7);
        }
        [data-mantine-color-scheme='dark'] & {
            color: var(--mantine-color-gray-5);
        }
    }
    & .sort-icon {
        margin-left: 3px;
        [data-mantine-color-scheme='light'] & {
            color: var(--mantine-color-gray-5);
        }
        [data-mantine-color-scheme='dark'] & {
            color: var(--mantine-color-gray-6);
        }
    }
    &.sorted .sort-icon  {      
        [data-mantine-color-scheme='light'] & {
            color: var(--mantine-color-gray-7);
        }
        [data-mantine-color-scheme='dark'] & {
            color: var(--mantine-color-gray-5);
        }
    }
`

const Pagination = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: var(--mantine-font-size-xs);
    padding-top: var(--mantine-spacing-md);
`

export const TableHead: React.FC<React.ComponentPropsWithoutRef<'thead'>> = ({ children, ...props }) => {
    return <Head {...props}>{children}</Head>
}

export const TableBody: React.FC<React.ComponentPropsWithoutRef<'tbody'>> = ({ children, ...props }) => {
    return <Body {...props}>{children}</Body>
}

export const TableRow: React.FC<React.ComponentPropsWithoutRef<'tr'>> = ({ className, children, ...props }) => {
    return <Row {...props} className={className}>{children}</Row>
}

export const TableCell: React.FC<React.ComponentPropsWithoutRef<'td'>> = ({ children, ...props }) => {
    return <Cell {...props}>{children}</Cell>
}

export const TableHeading: React.FC<React.ComponentPropsWithoutRef<'th'>> = ({ children, ...props }) => {
    return <Heading {...props}>{children}</Heading>
}

export const SortIconButton: React.FC<React.ComponentPropsWithoutRef<'a'>> = ({ className, children, ...props }) => {
    return <SortLink {...props} className={[className].join(' ')}>{children}</SortLink>
}

export const PaginationContainer: React.FC<React.ComponentPropsWithoutRef<'div'>> = ({ children, ...props }) => {
    return <Pagination {...props}>{children}</Pagination>
}

export const TableHeaderIcon: React.FC<IconProps> = (props) => {
    return (
        <Icon className="sort-icon" {...props} />
    )
}